import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Coins, Check, Zap, Star, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCredits } from '@/hooks/useCredits';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import { usePaymentPlatform } from '@/contexts/PaymentPlatformContext';
import { NativePaymentButton } from '@/components/NativePaymentButton';
import { syncPendingPurchases } from '@/services/revenueCatService';

// PayPal Production Client ID
const PAYPAL_CLIENT_ID = 'AZhTvr-H1ZT74t5JfqO7-P04fFQ4KRHanreP4Ycm8aQEV3siPcaHU7SiF2fI_-rWaWWYzmBrEoq8pm2_';

// PayPal Product IDs (Production) - Map credit packages to PayPal products
const PAYPAL_PRODUCT_IDS = {
  starter: 'PROD-8VH50242J5732902L',   // 10 Credits - $14.99
  basic: 'PROD-1DY7549056606223S',     // 25 Credits - $32.99
  premium: 'PROD-3ES45619WX123352E',   // 50 Credits - $49.99
  ultimate: 'PROD-6XV26261Y3472751W'   // 100 Credits - $89.99
} as const;

const PurchaseCredits = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { balance, purchaseCredits, packages } = useCredits();
  const [paypalReady, setPaypalReady] = useState(false);
  const { isNative, paymentMethod } = usePaymentPlatform();

  // Check if PayPal SDK loaded successfully
  useEffect(() => {
    const checkPayPal = () => {
      // @ts-ignore - PayPal SDK types
      if (typeof window !== 'undefined' && window.paypal) {
        setPaypalReady(true);
      }
    };
    
    // Check immediately and then every second for up to 10 seconds
    checkPayPal();
    const interval = setInterval(checkPayPal, 1000);
    const timeout = setTimeout(() => clearInterval(interval), 10000);
    
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
    }
  }, [user, navigate]);

  // Preventively sync pending purchases on native to clear "already owned" blocks
  useEffect(() => {
    if (isNative) {
      syncPendingPurchases();
    }
  }, [isNative]);

  // Helper to get the right price based on platform
  const getPackagePrice = (pkg: { price: number; nativePrice: number }) => {
    return isNative ? pkg.nativePrice : pkg.price;
  };

  const creditPackages = [
    {
      key: 'starter',
      credits: packages.starter.credits,
      price: getPackagePrice(packages.starter),
      popular: false,
      bestValue: false,
      features: [
        t('credits.packages.starter.feature1'),
        t('credits.packages.starter.feature2'),
      ],
    },
    {
      key: 'basic',
      credits: packages.basic.credits,
      price: getPackagePrice(packages.basic),
      popular: true,
      bestValue: false,
      features: [
        t('credits.packages.basic.feature1'),
        t('credits.packages.basic.feature2'),
        t('credits.packages.basic.feature3'),
      ],
    },
    {
      key: 'premium',
      credits: packages.premium.credits,
      price: getPackagePrice(packages.premium),
      popular: false,
      bestValue: false,
      features: [
        t('credits.packages.premium.feature1'),
        t('credits.packages.premium.feature2'),
        t('credits.packages.premium.feature3'),
      ],
    },
    {
      key: 'ultimate',
      credits: packages.ultimate.credits,
      price: getPackagePrice(packages.ultimate),
      popular: false,
      bestValue: true,
      features: [
        t('credits.packages.ultimate.feature1'),
        t('credits.packages.ultimate.feature2'),
        t('credits.packages.ultimate.feature3'),
        t('credits.packages.ultimate.feature4'),
      ],
    },
  ];

  const handleApprove = async (packageKey: string, transactionId: string, amount: number) => {
    try {
      await purchaseCredits.mutateAsync({
        package_type: packageKey as keyof typeof packages,
        payment_reference: transactionId,
        price_usd: amount,
      });

      navigate(`/credits-purchase-success?credits=${packages[packageKey as keyof typeof packages].credits}&reference=${transactionId}`);
    } catch (error: any) {
      toast({
        title: t('credits.purchase.error'),
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  if (!user) {
    return null;
  }

  return (
    <PayPalScriptProvider 
      options={{ 
        clientId: PAYPAL_CLIENT_ID,
        currency: 'USD',
        intent: 'capture',
      }}
    >
      <div className="min-h-screen bg-gradient-to-b from-background to-accent/5 py-8 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="mb-4"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('common.back')}
            </Button>
            
            <div className="text-center">
              <h1 className="text-4xl font-bold mb-2 flex items-center justify-center gap-2">
                <Coins className="h-8 w-8 text-primary" />
                {t('credits.purchase.title')}
              </h1>
              <p className="text-muted-foreground text-lg">
                {t('credits.purchase.subtitle')}
              </p>
            </div>
          </div>

          {/* Current Balance */}
          <Alert className="mb-8 max-w-2xl mx-auto">
            <Coins className="h-5 w-5" />
            <AlertDescription className="flex items-center justify-between">
              <span className="text-lg">
                {t('credits.purchase.currentBalance')}
              </span>
              <span className="text-2xl font-bold text-primary">
                {balance} {t('credits.title')}
              </span>
            </AlertDescription>
          </Alert>

          {/* PayPal Loading/Error Notice */}
          {!paypalReady && (
            <Alert className="mb-8 max-w-2xl mx-auto">
              <AlertDescription className="text-center">
                ⚠️ <strong>{t('common.error')}:</strong> {t('credits.paypalUnavailable')}
              </AlertDescription>
            </Alert>
          )}

          {/* Credit Packages Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {creditPackages.map((pkg) => (
              <Card 
                key={pkg.key}
                className={`relative ${
                  pkg.popular ? 'border-primary shadow-lg' : ''
                } ${pkg.bestValue ? 'border-green-500 shadow-lg' : ''}`}
              >
                {pkg.popular && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary">
                    <Star className="h-3 w-3 mr-1" />
                    {t('credits.purchase.mostPopular')}
                  </Badge>
                )}
                {pkg.bestValue && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-green-500">
                    <Zap className="h-3 w-3 mr-1" />
                    {t('credits.purchase.bestValue')}
                  </Badge>
                )}

                <CardHeader className="text-center pb-4">
                  <CardTitle className="text-2xl mb-2">
                    {t(`credits.packages.${pkg.key}.name`)}
                  </CardTitle>
                  <div className="text-4xl font-bold text-primary mb-1">
                    {pkg.credits}
                  </div>
                  <CardDescription className="text-lg">
                    {t('credits.title')}
                  </CardDescription>
                  <div className="text-2xl font-semibold mt-2">
                    ${pkg.price.toFixed(2)} USD
                  </div>
                </CardHeader>

                <CardContent>
                  <ul className="space-y-2 mb-6">
                    {pkg.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm">
                        <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="paypal-button-container">
                    {isNative ? (
                      <NativePaymentButton
                        tier="standard"
                        billingCycle="annual"
                        productType="credits"
                        creditPackage={pkg.key as 'starter' | 'basic' | 'premium' | 'ultimate'}
                        label={`${t('credits.purchase.buyNow')} - $${pkg.price.toFixed(2)}`}
                        onSuccess={() => {
                          navigate(`/credits-purchase-success?credits=${pkg.credits}&reference=native_purchase`);
                        }}
                        onError={(error) => {
                          toast({
                            title: t('credits.purchase.error'),
                            description: error.message,
                            variant: 'destructive',
                          });
                        }}
                      />
                    ) : paypalReady ? (
                      <PayPalButtons
                        style={{ 
                          layout: 'vertical',
                          shape: 'rect',
                          label: 'pay',
                        }}
                        createOrder={(data, actions) => {
                        return actions.order.create({
                          intent: 'CAPTURE',
                          purchase_units: [{
                            amount: {
                              currency_code: 'USD',
                              value: pkg.price.toFixed(2),
                            },
                            description: `${pkg.credits} ${t('credits.title')} - ${t(`credits.packages.${pkg.key}.name`)}`,
                            custom_id: user.id,
                            reference_id: PAYPAL_PRODUCT_IDS[pkg.key as keyof typeof PAYPAL_PRODUCT_IDS],
                          }],
                          application_context: {
                            brand_name: 'Petius',
                            shipping_preference: 'NO_SHIPPING',
                          },
                        });
                      }}
                      onApprove={async (data, actions) => {
                        const order = await actions.order!.capture();
                        const transactionId = order.id;
                        const amount = parseFloat(order.purchase_units[0].amount.value);
                        
                        await handleApprove(pkg.key, transactionId, amount);
                      }}
                        onError={(err) => {
                          console.error('PayPal error:', err);
                          toast({
                            title: t('credits.purchase.error'),
                            description: t('credits.purchase.paypalError'),
                            variant: 'destructive',
                          });
                        }}
                      />
                    ) : (
                      <Button disabled variant="outline" className="w-full">
                        {t('common.loading')}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* What can you do with credits */}
          <Card className="max-w-4xl mx-auto">
            <CardHeader>
              <CardTitle className="text-2xl text-center">
                {t('credits.purchase.whatYouCanDo')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="text-center p-4">
                  <div className="text-3xl mb-2">🐾</div>
                  <h3 className="font-semibold mb-1">{t('credits.usage.playdateFriendship')}</h3>
                  <p className="text-sm text-muted-foreground">1 {t('credits.credit')}</p>
                </div>
                <div className="text-center p-4">
                  <div className="text-3xl mb-2">🏠</div>
                  <h3 className="font-semibold mb-1">{t('credits.usage.adoption')}</h3>
                  <p className="text-sm text-muted-foreground">2 {t('credits.credits')}</p>
                </div>
                <div className="text-center p-4">
                  <div className="text-3xl mb-2">💕</div>
                  <h3 className="font-semibold mb-1">{t('credits.usage.breedingMatch')}</h3>
                  <p className="text-sm text-muted-foreground">4 {t('credits.credits')}</p>
                </div>
                <div className="text-center p-4">
                  <div className="text-3xl mb-2">⭐</div>
                  <h3 className="font-semibold mb-1">{t('credits.usage.superMatch')}</h3>
                  <p className="text-sm text-muted-foreground">5 {t('credits.credits')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Demo notice */}
          <div className="text-center mt-8 text-sm text-muted-foreground">
            {t('credits.purchase.securePayment')}
          </div>
        </div>
      </div>
    </PayPalScriptProvider>
  );
};

export default PurchaseCredits;